import SparkMD5 from 'spark-md5'
import { abortUploadFile, completeCloudUploadFile, importResourceSync, initCloudUploadFile } from '@/api/api'
import { getActiveCloudServerKey } from '@/utils/cloudServer'
import { getCloudUploadPageNumber } from '@/utils/cloudUploadPageCount'

export const MAX_CLOUD_UPLOAD_FILE_SIZE = 400 * 1024 * 1024

function extractApiBody(response) {
  if (response && response.data && typeof response.data == 'object') {
    return response.data
  }
  return response || {}
}

function extractApiData(response) {
  const body = extractApiBody(response)
  if (body && typeof body == 'object' && Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data || {}
  }
  return body && typeof body == 'object' ? body : {}
}

function assertApiSuccess(response, fallbackMessage) {
  const body = extractApiBody(response)
  if (!body || typeof body != 'object') {
    return body
  }
  const hasApiStatus = Object.prototype.hasOwnProperty.call(body, 'code')
    || Object.prototype.hasOwnProperty.call(body, 'success')
  if (!hasApiStatus) {
    return body
  }
  if (body && (body.code == 200 || body.success === true)) {
    return body
  }
  const message = body && (body.msg || body.message)
    ? (body.msg || body.message)
    : fallbackMessage
  throw new Error(message || 'Upload failed')
}

function pickString(source, keys) {
  if (!source || typeof source != 'object') {
    return ''
  }
  for (let i = 0; i < keys.length; i++) {
    const value = source[keys[i]]
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim()
    }
  }
  return ''
}

function pickObject(source, keys) {
  if (!source || typeof source != 'object') {
    return null
  }
  for (let i = 0; i < keys.length; i++) {
    const value = source[keys[i]]
    if (value && typeof value == 'object' && !Array.isArray(value)) {
      return value
    }
  }
  return null
}

function normalizeUploadHeaders(headers) {
  const normalized = {}
  if (!headers || typeof headers != 'object') {
    return normalized
  }
  Object.keys(headers).forEach(key => {
    const value = headers[key]
    if (value !== undefined && value !== null) {
      normalized[key] = String(value)
    }
  })
  return normalized
}

function hasUploadHeader(headers, headerName) {
  const normalizedName = String(headerName || '').toLowerCase()
  return Object.keys(headers || {}).some(key => String(key).toLowerCase() == normalizedName)
}

function isAliyunOssUploadUrl(parsedUrl) {
  if (!parsedUrl || !parsedUrl.hostname) {
    return false
  }
  const hostname = parsedUrl.hostname.toLowerCase()
  if (!/\.oss(?:-[a-z0-9-]+)?\.aliyuncs\.com$/.test(hostname)) {
    return false
  }
  const params = parsedUrl.searchParams
  return params.has('OSSAccessKeyId') || params.has('Signature') || params.has('x-oss-signature')
}

export function normalizeCloudUploadUrl(uploadUrl) {
  const rawUrl = String(uploadUrl || '').trim()
  if (!rawUrl) {
    return ''
  }
  try {
    const parsedUrl = new URL(rawUrl)
    if (!isAliyunOssUploadUrl(parsedUrl)) {
      return rawUrl
    }
    const normalizedPath = parsedUrl.pathname.replace(/%2F/ig, '/')
    if (normalizedPath == parsedUrl.pathname) {
      return rawUrl
    }
    return `${parsedUrl.protocol}//${parsedUrl.host}${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`
  } catch (error) {
    return rawUrl
  }
}

export function getCloudUploadRequestBody(file, uploadHeaders = {}) {
  if (!file || typeof file.slice != 'function' || hasUploadHeader(uploadHeaders, 'Content-Type')) {
    return file
  }
  return file.slice(0, file.size || undefined, '')
}

export function setCloudUploadHeaders(xhr, uploadHeaders = {}) {
  const headers = normalizeUploadHeaders(uploadHeaders)
  Object.keys(headers).forEach(key => {
    xhr.setRequestHeader(key, headers[key])
  })
}

function readXmlTag(xml, tagName) {
  const match = String(xml || '').match(new RegExp(`<${tagName}>([^<]*)</${tagName}>`, 'i'))
  return match && match[1] ? match[1] : ''
}

function getUploadErrorMessage(xhr, fallbackMessage) {
  const status = xhr && xhr.status ? xhr.status : 0
  const statusText = xhr && xhr.statusText ? xhr.statusText : ''
  const responseText = xhr && xhr.responseText ? xhr.responseText : ''
  const code = readXmlTag(responseText, 'Code')
  const message = readXmlTag(responseText, 'Message')
  if (code || message) {
    return `Upload failed (${status}${statusText ? ` ${statusText}` : ''}): ${[code, message].filter(Boolean).join(' - ')}`
  }
  return statusText || fallbackMessage || `Upload failed (${status})`
}

function normalizeHeaderValue(value) {
  return String(value || '').trim().replace(/^"|"$/g, '')
}

export function readCloudUploadEtag(xhr) {
  if (!xhr || typeof xhr.getResponseHeader != 'function') {
    return ''
  }

  const directValue = normalizeHeaderValue(
    xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || ''
  )
  if (directValue) {
    return directValue
  }

  if (typeof xhr.getAllResponseHeaders == 'function') {
    try {
      const headersText = String(xhr.getAllResponseHeaders() || '')
      const match = headersText.match(/(?:^|\r?\n)etag:\s*([^\r\n]*)/i)
      if (match && match[1]) {
        return normalizeHeaderValue(match[1])
      }
    } catch (error) {
      return ''
    }
  }

  return ''
}

function createMissingEtagError(xhr) {
  const status = xhr && xhr.status ? xhr.status : 0
  const statusText = xhr && xhr.statusText ? xhr.statusText : ''
  const prefix = `Upload completed (${status}${statusText ? ` ${statusText}` : ''})`
  return new Error(`${prefix}，但浏览器 JS 没有拿到 OSS 的 ETag。DevTools 里能看到响应头，不代表 XHR 可读；请在 OSS Bucket CORS 的 Exposed Headers / Access-Control-Expose-Headers 中加入 ETag。`)
}

function resolveUploadPath(uploadPath) {
  const first = Array.isArray(uploadPath) ? uploadPath[0] : uploadPath
  if (typeof first == 'string') {
    return { url: first, partNumber: '1', headers: {} }
  }
  if (first && typeof first == 'object') {
    return {
      url: pickString(first, ['url', 'uploadUrl', 'path', 'uploadPath']),
      partNumber: pickString(first, ['partNumber', 'partNo', 'part']) || '1',
      headers: normalizeUploadHeaders(pickObject(first, ['headers', 'uploadHeaders', 'requestHeaders']))
    }
  }
  return { url: '', partNumber: '1', headers: {} }
}

function normalizeFolderType(folderType) {
  return String(folderType || '').trim().replace(/^\/+|\/+$/g, '')
}

function buildUploadFolder(folderType) {
  const normalized = normalizeFolderType(folderType)
  return normalized ? `/${normalized}/` : '/'
}

function getResponseEtag(xhr) {
  return readCloudUploadEtag(xhr)
}

function uploadFileToUrl(file, uploadUrl, signal, onProgress, uploadHeaders = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let aborted = false
    const handleAbort = () => {
      aborted = true
      xhr.abort()
    }

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload canceled', 'AbortError'))
        return
      }
      signal.addEventListener('abort', handleAbort, { once: true })
    }

    xhr.open('PUT', normalizeCloudUploadUrl(uploadUrl), true)
    setCloudUploadHeaders(xhr, uploadHeaders)
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && typeof onProgress == 'function') {
        onProgress({
          loaded: event.loaded,
          total: event.total || file.size || 0
        })
      }
    }
    xhr.onload = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const eTag = getResponseEtag(xhr)
        if (!eTag) {
          reject(createMissingEtagError(xhr))
          return
        }
        resolve(eTag)
        return
      }
      reject(new Error(getUploadErrorMessage(xhr, `Upload failed (${xhr.status})`)))
    }
    xhr.onerror = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      reject(new Error('Upload failed'))
    }
    xhr.onabort = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      const error = new DOMException('Upload canceled', 'AbortError')
      if (aborted) {
        reject(error)
      } else {
        reject(error)
      }
    }
    xhr.send(getCloudUploadRequestBody(file, uploadHeaders))
  })
}

function readCloudUploadFileChunk(chunk) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => resolve(event && event.target ? event.target.result : null)
    reader.onerror = () => reject(reader.error || new Error('File read failed'))
    reader.readAsArrayBuffer(chunk)
  })
}

async function getCloudUploadFileHash(file) {
  const chunkSize = 100 * 1024 * 1024
  const fileSize = Number(file && file.size) || 0
  const chunks = Math.max(1, Math.ceil(fileSize / chunkSize))
  const spark = new SparkMD5.ArrayBuffer()

  for (let index = 0; index < chunks; index++) {
    const start = index * chunkSize
    const end = Math.min(start + chunkSize, fileSize)
    const buffer = await readCloudUploadFileChunk(file.slice(start, end))
    spark.append(buffer)
  }

  return spark.end()
}

async function uploadCloudResourceSideFile(options = {}) {
  const file = options.file
  if (!file) {
    return ''
  }
  const fileName = options.fileName || file.name
  const folderType = normalizeFolderType(options.folderType)
  const folder = buildUploadFolder(folderType)
  const serverKey = options.serverKey || getActiveCloudServerKey()
  const signal = options.signal
  const appType = Number(options.appType) || 0

  const initResponse = await initCloudUploadFile({
    fileName,
    partNum: 1,
    type: folder
  }, { serverKey, signal })
  assertApiSuccess(initResponse, 'Upload initialization failed')

  const initData = extractApiData(initResponse)
  const uploadId = pickString(initData, ['uploadId', 'id'])
  const initFilePath = pickString(initData, ['filePath', 'path'])
  if (typeof options.onInitialized == 'function') {
    await options.onInitialized({ uploadId, filePath: initFilePath })
  }
  if (signal && signal.aborted) {
    throw createCloudUploadAbortError()
  }

  const uploadPath = resolveUploadPath(initData.uploadPath)
  if (!uploadPath.url) {
    throw new Error('Upload initialization failed')
  }

  const eTag = await uploadFileToUrl(file, uploadPath.url, signal, options.onProgress, uploadPath.headers)
  const resolvedEtag = String(eTag || '').trim()
  if (!resolvedEtag) {
    throw new Error('Upload completion failed: missing OSS ETag. DevTools 里看得到不等于 XHR 里可读，请检查 OSS CORS Exposed Headers 是否包含 ETag。')
  }
  const eTags = {}
  eTags[String(uploadPath.partNumber || '1')] = resolvedEtag

  const completeResponse = await completeCloudUploadFile({
    uploadId,
    folder,
    source: String(appType),
    filePath: initFilePath,
    fileName,
    hash: await getCloudUploadFileHash(file),
    eTags
  }, { serverKey, signal })
  assertApiSuccess(completeResponse, 'Upload completion failed')

  const completeData = extractApiData(completeResponse)
  return pickString(completeData, ['fileURL', 'fileUrl', 'file_url', 'filePath', 'path', 'url'])
    || initFilePath
    || ''
}

function shouldIncludeImportCoverField(options = {}) {
  return options.includeCoverField === true || typeof options.resolveCoverFile == 'function'
}

async function resolveImportCoverPath(options = {}) {
  if (!shouldIncludeImportCoverField(options)) {
    return undefined
  }
  try {
    const coverFile = typeof options.resolveCoverFile == 'function'
      ? await options.resolveCoverFile(options)
      : null
    if (!coverFile) {
      return ''
    }
    if (options.signal && options.signal.aborted) {
      throw createCloudUploadAbortError()
    }
    if (typeof options.onPhase == 'function') {
      options.onPhase('covering')
    }
    return await uploadCloudResourceSideFile({
      file: coverFile,
      fileName: options.coverFileName || coverFile.name || `cover-${Date.now()}.jpg`,
      folderType: options.coverFolderType || options.folderType,
      appType: options.appType,
      serverKey: options.serverKey,
      signal: options.signal,
      onInitialized: options.onCoverInitialized
    })
  } catch (error) {
    if (isCloudUploadAbort(error)) {
      throw error
    }
    if (typeof console != 'undefined' && typeof console.warn == 'function') {
      console.warn('[cloudUpload] Failed to upload cover:', error)
    }
    return ''
  }
}

export function isCloudUploadAbort(error) {
  return !!(error && (error.name == 'AbortError' || error.code == 'ERR_CANCELED'))
}

function createCloudUploadAbortError() {
  const error = new Error('Upload canceled')
  error.name = 'AbortError'
  return error
}

export function abortCloudResourceFile(options = {}) {
  const uploadId = String(options.uploadId || '').trim()
  const filePath = String(options.filePath || '').trim()
  if (!uploadId) {
    return Promise.resolve()
  }
  try {
    const parameter = { uploadId }
    if (filePath) {
      parameter.filePath = filePath
    }
    return Promise.resolve(abortUploadFile(parameter, undefined, {
      serverKey: options.serverKey
    }))
  } catch (error) {
    return Promise.reject(error)
  }
}

export async function uploadCloudResourceFile(options = {}) {
  const file = options.file
  if (!file) {
    throw new Error('Missing upload file')
  }
  const fileName = options.fileName || file.name
  const folderType = normalizeFolderType(options.folderType)
  const folder = buildUploadFolder(folderType)
  const serverKey = options.serverKey || getActiveCloudServerKey()
  const signal = options.signal
  const appType = Number(options.appType) || 0

  const initResponse = await initCloudUploadFile({
    fileName,
    partNum: 1,
    type: folder
  }, { serverKey })
  assertApiSuccess(initResponse, 'Upload initialization failed')

  const initData = extractApiData(initResponse)
  const uploadId = pickString(initData, ['uploadId', 'id'])
  const initFilePath = pickString(initData, ['filePath', 'path'])
  if (typeof options.onInitialized == 'function') {
    await options.onInitialized({ uploadId, filePath: initFilePath })
  }
  if (signal && signal.aborted) {
    throw createCloudUploadAbortError()
  }
  if (typeof options.onPhase == 'function') {
    options.onPhase('uploading')
  }
  const uploadPath = resolveUploadPath(initData.uploadPath)
  if (!uploadPath.url) {
    throw new Error('Upload initialization failed')
  }

  const eTag = await uploadFileToUrl(file, uploadPath.url, signal, options.onProgress, uploadPath.headers)
  const resolvedEtag = String(eTag || '').trim()
  if (!resolvedEtag) {
    throw new Error('Upload completion failed: missing OSS ETag. DevTools 里看得到不等于 XHR 里可读，请检查 OSS CORS Exposed Headers 是否包含 ETag。')
  }
  const eTags = {}
  eTags[String(uploadPath.partNumber || '1')] = resolvedEtag

  if (typeof options.onPhase == 'function') {
    options.onPhase('completing')
  }

  const completeResponse = await completeCloudUploadFile({
    uploadId,
    folder,
    source: String(appType),
    filePath: initFilePath,
    fileName,
    hash: await getCloudUploadFileHash(file),
    eTags
  }, { serverKey, signal })
  assertApiSuccess(completeResponse, 'Upload completion failed')

  const completeData = extractApiData(completeResponse)
  const filePath = pickString(completeData, ['fileURL', 'fileUrl', 'file_url', 'filePath', 'path', 'url'])
    || initFilePath
  if (!filePath) {
    throw new Error('Upload completion failed')
  }

  const includeCoverField = shouldIncludeImportCoverField(options)
  let importPhaseEmitted = false
  if (!includeCoverField && typeof options.onPhase == 'function') {
    options.onPhase('importing')
    importPhaseEmitted = true
  }
  const pageNumber = await getCloudUploadPageNumber(file, fileName)
  const coverPath = await resolveImportCoverPath({
    ...options,
    file,
    fileName,
    folderType,
    appType,
    serverKey,
    signal
  })
  if (!importPhaseEmitted && typeof options.onPhase == 'function') {
    options.onPhase('importing')
  }

  const importPayload = {
    appType,
    name: fileName,
    parentId: options.parentId || '',
    fileSize: file.size || 0,
    filePath,
    pageNumber
  }
  if (includeCoverField) {
    importPayload.cover = coverPath || ''
  }

  const importResponse = await importResourceSync(importPayload, { serverKey, signal })
  assertApiSuccess(importResponse, 'Resource import failed')

  if (typeof options.onPhase == 'function') {
    options.onPhase('done')
  }

  return {
    filePath,
    initData,
    completeData,
    importData: extractApiData(importResponse)
  }
}
